import type { ApiClient } from '../../core/api-client.js';
import { ApiError } from '../../core/api-client.js';
import type { Resultado } from '../../core/resultado.js';
import type {
  PulsoNegocio,
  CentralDeComando,
  OrigemVendaParceiro,
  FunilProducao,
  PontoHeatmapEnvios,
  PrevisaoCaixa,
  CupomMaisUsado,
  ResumoClientes,
} from '@pgcustom/shared-types';

/** Card 'Vendas por origem' — formato de GET /relatorios/vendas-por-origem. */
export interface VendasPorOrigem {
  disponivel: boolean;
  motivo?: string;
  dias: number;
  canais?: Array<{ canal: string; pedidos: number; valorCentavos: number }>;
  campanhas?: Array<{ campanha: string; pedidos: number; valorCentavos: number }>;
  totalPedidos?: number;
  totalCentavos?: number;
  atualizadoEm?: string;
}

/** Card 'Site e tráfego' (GA4 do site cruzado com pedidos pagos do ERP) — formato de GET /relatorios/analytics-site. */
export interface AnalyticsSite {
  disponivel: boolean;
  motivo?: string;
  dias: number;
  visitas?: { sessoes: number; usuarios: number; novosUsuarios: number };
  origens?: Array<{ canal: string; sessoes: number }>;
  funil?: Array<{ evento: string; rotulo: string; quantidade: number }>;
  atendimento?: { chatAberto: number; whatsapp: number; contatos?: number; orcamentos?: number };
  pedidosPagosErp?: { quantidade: number; valorCentavos: number };
  conversaoPercentual?: number | null;
  atualizadoEm?: string;
}

/**
 * Adapter do módulo Relatórios no frontend — único ponto que conhece o
 * formato de `/api/v1/relatorios/*`
 * (`apps/api/src/modules/relatorios/relatorios.routes.ts`, Sprint 14).
 * Todo método é `GET`, nenhum efeito colateral.
 */
export class RelatoriosApi {
  constructor(private readonly api: ApiClient) {}

  private async buscar<TChave extends string, TDado>(
    caminho: string,
    chave: TChave,
    consulta?: Record<string, string | number>
  ): Promise<Resultado<TDado>> {
    try {
      const resposta = await this.api.get<Record<TChave, TDado>>(caminho, consulta);
      return { status: 'ok', dado: resposta[chave] };
    } catch (erro) {
      if (erro instanceof ApiError && erro.status === 403) return { status: 'sem-permissao' };
      return { status: 'erro', mensagem: erro instanceof ApiError ? erro.message : 'falha ao carregar relatório' };
    }
  }

  async obterPulsoDoNegocio(): Promise<Resultado<PulsoNegocio>> {
    return this.buscar('/relatorios/pulso-negocio', 'pulso');
  }

  async obterCentralDeComando(): Promise<Resultado<CentralDeComando>> {
    return this.buscar('/relatorios/central-de-comando', 'centralDeComando');
  }

  async obterOrigemVendasPorParceiro(meses = 6): Promise<Resultado<OrigemVendaParceiro[]>> {
    const resultado = await this.buscar<'origemVendas', OrigemVendaParceiro[]>(
      '/relatorios/origem-vendas-parceiro',
      'origemVendas',
      { meses }
    );
    if (resultado.status === 'ok' && resultado.dado.length === 0) return { status: 'vazio' };
    return resultado;
  }

  async obterFunilProducao(dias?: number): Promise<Resultado<FunilProducao>> {
    return this.buscar('/relatorios/funil-producao', 'funil', dias === undefined ? undefined : { dias });
  }

  async obterHeatmapEnvios(dias = 90): Promise<Resultado<PontoHeatmapEnvios[]>> {
    const resultado = await this.buscar<'heatmap', PontoHeatmapEnvios[]>('/relatorios/heatmap-envios', 'heatmap', {
      dias,
    });
    if (resultado.status === 'ok' && resultado.dado.length === 0) return { status: 'vazio' };
    return resultado;
  }

  async obterPrevisaoCaixa(): Promise<Resultado<PrevisaoCaixa>> {
    return this.buscar('/relatorios/previsao-caixa', 'previsaoCaixa');
  }

  async obterCuponsMaisUsados(limite = 5): Promise<Resultado<CupomMaisUsado[]>> {
    const resultado = await this.buscar<'cupons', CupomMaisUsado[]>('/relatorios/cupons-mais-usados', 'cupons', {
      limite,
    });
    if (resultado.status === 'ok' && resultado.dado.length === 0) return { status: 'vazio' };
    return resultado;
  }

  async obterResumoClientes(): Promise<Resultado<ResumoClientes>> {
    return this.buscar('/relatorios/resumo-clientes', 'resumo');
  }

  async obterAnalyticsSite(dias: 7 | 30 = 7): Promise<Resultado<AnalyticsSite>> {
    return this.buscar('/relatorios/analytics-site', 'analytics', { dias });
  }

  async obterVendasPorOrigem(dias: 30 | 90 = 30): Promise<Resultado<VendasPorOrigem>> {
    return this.buscar('/relatorios/vendas-por-origem', 'vendas', { dias });
  }
}
